import { useChallenges } from '@/src/store/challenge.context';
import { Alert, Box, Button, Header, SpaceBetween, Spinner, Toggle } from '@amzn/awsui-components-react';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ChallengeSelection.scss';
import { useToolPanel } from '../../../store/tool-panel.context';
import { useUser } from '../../../store/user.context';
import { Campaign } from '../../../types/Campaign';
import {
  ChallengeDescriptor,
  ChallengeListItem,
  ChallengeWarningDictionary,
  ChallengeWarnings,
  getChallengePointsPossible,
} from '../../../types/Challenge';
import { ChallengeBoardPosition } from '../../../types/ChallengeBoard';
import { Nullable } from '../../../types/common';
import { Event } from '../../../types/Event';
import { i18nKeys } from '../../../utils/i18n.utils';
import { getDuration } from '../../../utils/time.utils';
import { HorizontalRule } from '../HorizontalRule';
import ChallengeCard from './ChallengeCard';
import ChallengeDataImportModal from './ChallengeDataImportModal';
import { useEditEvent } from '@/src/store/edit-event.context';
import { arraysAreTheSameRegardlessOfOrder } from '@/src/utils/string.utils';
import { getAllChallengeIdsFromEvent } from '@/src/utils/event-campaign-common.utils';

interface ChallengeSelectionProps {
  handleRemoveChallenge?: (challenges: ChallengeDescriptor[]) => void;
  toggleChallengeInfo: (challengeListItem: ChallengeListItem) => void;
  targetChallengeDescriptors: ChallengeDescriptor[];
  target: Event | Campaign;
}

interface DragItem {
  challengeId: string | null;
  index: number;
}

const ChallengeSelection: React.FC<ChallengeSelectionProps> = ({
  handleRemoveChallenge,
  target,
  targetChallengeDescriptors,
  toggleChallengeInfo,
}) => {
  const {
    getChallengesByIds,
    selectedChallengesById,
    wrapperMapInitalized,
    copyChallengeDescriptorsFromTarget,
    challengeWrappers,
    getChallengeDescriptor,
    gameBoardPositions,
    updateGameBoardPositions,
    loadingChallenges,
  } = useChallenges();
  const { t } = useTranslation();
  const { user } = useUser();
  const [selectedChallenges, setSelectedChallenges] = useState<Nullable<ChallengeListItem>[] | undefined>(undefined);
  const [maxTotalScore, setMaxTotalScore] = useState(0);
  const [estimatedTotalSolveTime, setEstimatedTotalSolveTime] = useState('0m');
  const [advancedMode, setAdvancedMode] = useState(false);
  const [importTeamPropertiesVisible, setImportTeamPropertiesVisible] = useState(false);

  const { toggleShowToolPanel } = useToolPanel();

  const isEvent = target instanceof Event;

  useEffect(() => {
    if (isEvent && target.challengeBoards) {
      const targetChallengeBoard = target.challengeBoards[0];
      const newGameBoardPositions: { [id: string]: Nullable<ChallengeBoardPosition> } = {};
      // initialize board positions
      (targetChallengeBoard?.challengeIds || []).forEach((challengeId) => {
        newGameBoardPositions[challengeId] = targetChallengeBoard.getBoardPosition(challengeId);
      });
      updateGameBoardPositions(newGameBoardPositions);
    }
  }, []);

  useEffect(() => {
    copyChallengeDescriptorsFromTarget(
      targetChallengeDescriptors,
      isEvent && target.challengeBoards ? target.challengeBoards[0] : undefined
    );
  }, [wrapperMapInitalized, challengeWrappers, gameBoardPositions]);

  useEffect(() => {
    getMaxTotalScore();
    getAvgTimeToSolveAllChallenges();

    return () => toggleShowToolPanel(false);
  }, [selectedChallenges]);

  // keep the max score in sync when a challenge's scoring overrides change
  useEffect(() => {
    getMaxTotalScore();
  }, [targetChallengeDescriptors]);

  const getIdsFromChallengeList = useMemo(
    () => (challenges: { challengeId?: string | null }[]) =>
      challenges.map((c) => c?.challengeId || '').filter((id) => !!id),
    []
  );

  useEffect(() => {
    const targetChallengeIds = getIdsFromChallengeList(targetChallengeDescriptors);
    if (
      !selectedChallengesById?.length ||
      !arraysAreTheSameRegardlessOfOrder(getIdsFromChallengeList(selectedChallengesById), targetChallengeIds)
    ) {
      const challengeIds = getIdsFromChallengeList(targetChallengeDescriptors);
      if (target instanceof Event) {
        challengeIds.push(...getAllChallengeIdsFromEvent(target));
      }
      getChallengesByIds(false, false, [...new Set(challengeIds)], true);
    }
  }, [targetChallengeDescriptors]);

  useEffect(() => {
    if (selectedChallengesById?.length) {
      // sort in the same order as the event challenge descriptors
      const challenges = target.challengeDescriptors
        .map((c) => c.challengeId)
        .reduce((accum, descriptorChallengeId) => {
          const found = selectedChallengesById.find((c) => c?.challengeId === descriptorChallengeId);
          if (found) {
            accum.push(found);
          }
          return accum;
        }, [] as ChallengeListItem[]);
      setSelectedChallenges(challenges);
    }
  }, [selectedChallengesById]);

  /**
   * Resolves the challenge descriptor used to display a challenge's scoring.
   * The target's own descriptor is the source of truth for overrides (eg. points possible), so it
   * is preferred over the challenge context's descriptor map, which is only populated once the full
   * challenge list has been loaded and can therefore be missing or stale on this page.
   */
  const resolveChallengeDescriptor = (
    challenge: ChallengeListItem,
    challengeId: string
  ): Nullable<ChallengeDescriptor> => {
    const gameBoard = isEvent && target.challengeBoards ? target.challengeBoards[0] : undefined;

    // game board challenges take their score from the board, which the challenge context applies
    if (gameBoard && gameBoardPositions?.[challengeId]) {
      return getChallengeDescriptor(challenge, gameBoard);
    }

    return (
      targetChallengeDescriptors.find((cd) => cd.challengeId === challengeId) ??
      getChallengeDescriptor(challenge, gameBoard)
    );
  };

  const getCurrentChallengePointsPossible = (challenge: Nullable<ChallengeListItem>): number => {
    if (!challenge || !challenge.challengeId) {
      return 0;
    }

    const cd: Nullable<ChallengeDescriptor> = resolveChallengeDescriptor(challenge, challenge.challengeId);
    if (cd) {
      return getChallengePointsPossible(target.getScoringSettings(), challenge, cd);
    } else {
      return 0;
    }
  };

  const getChallengeDefaultPointsPossible = (challenge: ChallengeListItem): number => {
    if (!challenge || !challenge.challengeId) {
      return 0;
    }
    const cd: Nullable<ChallengeDescriptor> = resolveChallengeDescriptor(challenge, challenge.challengeId);

    return cd ? getChallengePointsPossible(target.getScoringSettings(), challenge, cd, false) : 0;
  };

  const getMaxTotalScore = (): void => {
    setMaxTotalScore(
      (selectedChallenges || [])
        .map((challengeListItem: Nullable<ChallengeListItem>) => getCurrentChallengePointsPossible(challengeListItem))
        .reduce((total: number, score: number) => total + score, 0)
    );
  };

  const getAvgTimeToSolveAllChallenges = (): void => {
    setEstimatedTotalSolveTime(
      getDuration(
        (selectedChallenges || [])
          .map((challengeListItem) => {
            let avgSolveTimeInSeconds: number = challengeListItem?.globalStatistics.solveTimes.trimmedAvgSeconds || 0;
            if (!avgSolveTimeInSeconds) {
              avgSolveTimeInSeconds = 60 * 45; // 45 minutes as default
            }
            return avgSolveTimeInSeconds;
          })
          .reduce((total, score) => total + score, 0),
        false
      ) || '0m'
    );
  };

  const handleRemoveChallengeAction = (challengeId: string) => {
    if (handleRemoveChallenge) {
      const challengeToRemoveIndex = targetChallengeDescriptors.findIndex(
        (challengeDescriptor: ChallengeDescriptor) => challengeDescriptor.challengeId === challengeId
      );
      targetChallengeDescriptors.splice(challengeToRemoveIndex, 1);
      if (
        isEvent &&
        target.backupChallengeConfig?.perChallengeBackups &&
        target.backupChallengeConfig?.perChallengeBackups[challengeId]?.length > 0
      ) {
        target.backupChallengeConfig.removePerChallengeBackups(challengeId);
      }
      handleRemoveChallenge(_.clone(targetChallengeDescriptors));
    }
  };

  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const { editMode } = useEditEvent();

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem || !selectedChallenges) return;

    const newChallenges = [...selectedChallenges];
    const draggedChallenge = newChallenges[draggedItem.index];

    // Remove the dragged item from its original position
    newChallenges.splice(draggedItem.index, 1);
    // Insert it at the new position
    newChallenges.splice(dropIndex, 0, draggedChallenge);

    setSelectedChallenges(newChallenges);

    // Update the challenge descriptors to match the new order
    if (handleRemoveChallenge) {
      const newChallengeDescriptors = newChallenges
        .map((challenge) => {
          if (!challenge) return null;
          return targetChallengeDescriptors.find((desc) => desc.challengeId === challenge.challengeId);
        })

        .filter((desc): desc is ChallengeDescriptor => desc !== null);

      handleRemoveChallenge(newChallengeDescriptors);
    }
  };

  const renderSelectedChallengesList = () => {
    if (loadingChallenges) {
      return (
        <Box textAlign="center" margin={{ top: 'l' }}>
          <Spinner size="large" />
        </Box>
      );
    }

    if (!selectedChallenges?.length) {
      return (
        <Box textAlign="center" margin={{ top: 'l' }}>
          <h2>{t(i18nKeys.challenges.challengeSelection.noChallengesSelected)}</h2>
        </Box>
      );
    }

    return (
      <>
        <SpaceBetween size={'l'} direction="horizontal">
          <Box variant="h2">
            {t(i18nKeys.challenges.challengeSelection.maxScore.label)}
            {t(i18nKeys.challenges.challengeSelection.maxScore.value, { points: maxTotalScore })}
          </Box>
          <Box variant="h2">
            {t(i18nKeys.challenges.challengeSelection.estimatedTime)}
            {estimatedTotalSolveTime}
          </Box>
        </SpaceBetween>
        <SpaceBetween direction="vertical" size="s" data-testid="challenge-selection">
          {selectedChallenges?.map((challengeItem: Nullable<ChallengeListItem>, index: number) => {
            return (
              challengeItem &&
              challengeItem.challengeId && (
                <div
                  key={`selected-challenge-${index}`}
                  onDragOver={(e) => {
                    if (editMode) {
                      e.preventDefault();
                      if (e.dataTransfer) {
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }
                  }}
                  onDrop={(e) => {
                    if (editMode) {
                      handleDrop(e, index);
                    }
                  }}
                  role="listitem"
                  className="challenge-item"
                >
                  {editMode && (
                    <div
                      draggable
                      onDragStart={(e) => {
                        setDraggedItem({ challengeId: challengeItem.challengeId, index });
                        // Add dragging class to the parent container for visual feedback
                        const parentElement = e.currentTarget.parentElement;
                        if (parentElement) {
                          parentElement.classList.add('dragging');

                          // Calculate the position of the drag handle within the parent element
                          const handleRect = e.currentTarget.getBoundingClientRect();
                          const parentRect = parentElement.getBoundingClientRect();

                          // Calculate offsets to keep the cursor over the drag handle
                          // We add half the width of the handle to center it horizontally
                          const xOffset = handleRect.left - parentRect.left + handleRect.width / 2;
                          // We add half the height of the handle to center it vertically
                          const yOffset = handleRect.top - parentRect.top + handleRect.height / 2;

                          // Set the drag image to be the entire card container with calculated offsets
                          // Only use setDragImage if it's available (it's not available in test environment)
                          if (e.dataTransfer && typeof e.dataTransfer.setDragImage === 'function') {
                            e.dataTransfer.setDragImage(parentElement, xOffset, yOffset);
                          }
                        }
                        if (e.dataTransfer) {
                          e.dataTransfer.effectAllowed = 'move';
                        }
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.parentElement?.classList.remove('dragging');
                        setDraggedItem(null);
                      }}
                      className="drag-handle"
                      aria-label={`Drag to reorder ${challengeItem.props.title || ''}`}
                      aria-hidden="false"
                    >
                      <div className="drag-handle-line" />
                      <div className="drag-handle-line" />
                      <div className="drag-handle-line" />
                    </div>
                  )}
                  <div className="challenge-card-wrapper">
                    <ChallengeCard
                      key={`selected-challenge-${index}`}
                      challenge={challengeItem}
                      index={index}
                      getChallengePointsPossible={getCurrentChallengePointsPossible}
                      getDefaultChallengePointsPossible={getChallengeDefaultPointsPossible}
                      handleRemoveChallengeAction={handleRemoveChallengeAction}
                      toggleChallengeInfo={toggleChallengeInfo}
                      advancedMode={advancedMode}
                      challengeDescriptor={
                        target.challengeDescriptors.find((ch) => ch.challengeId === challengeItem.challengeId) || null
                      }
                      target={target}
                    />
                  </div>
                </div>
              )
            );
          })}
        </SpaceBetween>
      </>
    );
  };

  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        counter={selectedChallenges ? `(${selectedChallenges?.length})` : '(0)'}
        data-testid="event-challenge_selected-challenges-header"
        actions={
          <SpaceBetween direction="horizontal" size="m" alignItems="center">
            {isEvent && user && (target.isEventOwner(user) || target.isEventFacilitatorOrSupport(user)) && (
              <Button
                iconName="upload"
                key="import-team-properties"
                variant="primary"
                onClick={() => setImportTeamPropertiesVisible(true)}
                disabled={!target.canEditAttribute('importTeamProperties', user)}
              >
                {t(i18nKeys.challenges.importChallenges.buttons.importTeamProperties)}
              </Button>
            )}
            <Toggle checked={advancedMode} onChange={() => setAdvancedMode(!advancedMode)}>
              {t(i18nKeys.challenges.challengeSelection.labels.advancedMode)}
            </Toggle>
          </SpaceBetween>
        }
      >
        {t(i18nKeys.challenges.selectedChallenges)}
      </Header>
      {isEvent && (
        <SpaceBetween size="s">
          <Alert
            type="info"
            statusIconAriaLabel="info"
            header={t(i18nKeys.challenges.challengeSelection.challengeOrder.header)}
          >
            {t(i18nKeys.challenges.challengeSelection.challengeOrder.description)}
          </Alert>
          {target?.warnings &&
            Object.keys(target.warnings.challengeWarnings).length > 0 &&
            Object.keys(target?.warnings.challengeWarnings).map((challengeWarning: string, index) => {
              return (
                <Alert type="warning" key={`challenge-warning-${index}`}>
                  {t(ChallengeWarningDictionary[challengeWarning as ChallengeWarnings], {
                    challengeIds: target.warnings.challengeWarnings[challengeWarning],
                  })}
                </Alert>
              );
            })}
        </SpaceBetween>
      )}
      <HorizontalRule />
      {renderSelectedChallengesList()}
      {isEvent && (
        <ChallengeDataImportModal
          target={target}
          isVisible={importTeamPropertiesVisible}
          toggleVisible={setImportTeamPropertiesVisible}
          challengeIds={target.challengeDescriptors.map((cd: ChallengeDescriptor) => cd.challengeId || '')}
        />
      )}
    </SpaceBetween>
  );
};
export default ChallengeSelection;
